import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Lock, Users } from "lucide-react";

/**
 * Landing page for a group invite link. Shows a preview of the group so the
 * visitor can decide before accepting.
 */
export default function GroupJoinPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, err: "", group: null, already_member: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/groups/invite/${token}`)
      .then(r => setState({ loading: false, err: "", ...r.data }))
      .catch(e => setState({ loading: false, err: formatErr(e), group: null, already_member: false }));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/groups/invite/${token}/accept`);
      if (data.requires_subscription) {
        toast("This group is paid — redirecting to checkout.");
        const sub = await api.post(`/groups/${data.group_id}/subscribe`);
        window.location.href = sub.data.url;
        return;
      }
      toast.success("Welcome!");
      nav(`/groups/${data.group_id}`);
    } catch (e) { toast.error(formatErr(e)); setBusy(false); }
  };

  if (state.loading) return <div className="p-16 text-center text-[#8A8A8A]"><Loader2 size={16} className="inline animate-spin"/> Loading invite…</div>;
  if (state.err) return <div className="p-16 text-center text-[#B84B4B]">{state.err}</div>;

  const g = state.group;
  const isPaid = Number(g.price_monthly) > 0;

  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="group-join-page">
      <div className="card p-6 text-center">
        {g.avatar
          ? <img src={g.avatar} alt="" className="h-16 w-16 rounded-2xl object-cover mx-auto"/>
          : <div className="h-16 w-16 rounded-2xl bg-[#EEF3FF] text-[#0057FF] inline-flex items-center justify-center mx-auto"><Users size={22}/></div>}
        <p className="overline mt-3">You're invited to</p>
        <h1 className="text-2xl font-heading mt-1">{g.name}</h1>
        {g.description && <p className="text-sm text-[#4A4A4A] mt-2">{g.description}</p>}
        {isPaid && (
          <p className="text-xs text-[#0057FF] mt-3 inline-flex items-center gap-1"><Lock size={11}/> Paid group · ${Number(g.price_monthly).toFixed(0)}/month</p>
        )}
        {state.already_member ? (
          <button onClick={() => nav(`/groups/${g.id}`)} className="btn-primary w-full mt-5" data-testid="group-join-open">Open group</button>
        ) : (
          <button onClick={accept} disabled={busy} className="btn-primary w-full mt-5" data-testid="group-join-accept">
            {busy ? <Loader2 size={14} className="animate-spin"/> : (isPaid ? "Continue to checkout" : "Accept & join")}
          </button>
        )}
      </div>
    </div>
  );
}
